import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatDatepicker } from '@angular/material/datepicker';
import { takeUntil } from 'rxjs';
import { Destroyer } from 'src/app/core/abstract/destroyer';
import { adjustTime } from 'src/app/core/utils/date-utils';

type CalendarDate = Date | string | null | undefined;

@Component({
  selector: 'craft-calendar',
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CraftCalendarComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class CraftCalendarComponent extends Destroyer implements ControlValueAccessor, OnInit, OnChanges {
  @ViewChild('datepicker', { static: true }) datepicker: MatDatepicker<any>;
  @ViewChild('datepickerFooter', { static: true }) datepickerFooter: ElementRef;

  @Input() public placeholder = 'Set Date';
  @Input() public value: CalendarDate;
  @Input() public hideIcon = false;
  @Output() public change = new EventEmitter<CalendarDate>();

  public date = new FormControl<CalendarDate>(null);

  public onTouched = () => {};
  public onChange = (_: CalendarDate) => {};

  public disabled = false;

  constructor() {
    super();
  }

  public ngOnInit(): void {
    this.date.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.sendValue(value);
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.value) {
      this.date.setValue(changes.value.currentValue, { emitEvent: false });
    }
  }

  public onOpen(): void {
    this.appendFooter();
    this.onTouched();
  }

  public done(): void {
    this.datepicker?.close();
  }

  public clear(): void {
    this.sendValue(null);
    this.date.setValue(null);
    this.datepicker?.close();
  }

  public writeValue(value: CalendarDate): void {
    this.date.setValue(value, { emitEvent: false });
  }

  public sendValue(value: CalendarDate) {
    const dt = adjustTime(value);
    this.change.next(dt);
    this.onChange(dt);
    this.onTouched();
  }

  public registerOnChange(fn: (_: CalendarDate) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  private appendFooter() {
    const dpContent = document.getElementsByTagName('mat-datepicker-content')[0];
    if (dpContent && this.datepickerFooter?.nativeElement) {
      dpContent.appendChild(this.datepickerFooter.nativeElement);
    }
  }
}
